import { supabase } from '../lib/supabase';

// A counsellor is considered "online" if their last_seen was within the last 5 minutes
const ONLINE_THRESHOLD_MINUTES = 5;

export const counsellorPresenceService = {
  /**
   * Update the current user's last_seen timestamp (call periodically while logged in)
   */
  async updatePresence(userId) {
    if (!userId) return;
    try {
      await supabase
        ?.from('users')
        ?.update({ last_seen: new Date()?.toISOString() })
        ?.eq('id', userId);
    } catch (error) {
      console.error('Error updating presence:', error);
    }
  },

  /**
   * Check if a counsellor (institute user) is currently online
   * Returns true if their last_seen is within ONLINE_THRESHOLD_MINUTES
   */
  async isCounsellorOnline(instituteUserId) {
    if (!instituteUserId) return false;
    try {
      const { data, error } = await supabase
        ?.from('users')?.select('last_seen, role')?.eq('id', instituteUserId)
        ?.single();

      if (error || !data) return false;

      // Only track presence for institute role
      if (data?.role !== 'institute') return false;

      if (!data?.last_seen) return false;

      const lastSeen = new Date(data.last_seen);
      const now = new Date();
      const diffMinutes = (now - lastSeen) / (1000 * 60);

      return diffMinutes <= ONLINE_THRESHOLD_MINUTES;
    } catch (error) {
      console.error('Error checking counsellor presence:', error);
      return false;
    }
  },

  /**
   * Subscribe to real-time presence changes for a counsellor
   * Returns an unsubscribe function
   */
  subscribeToPresence(instituteUserId, onStatusChange) {
    if (!instituteUserId) return () => {};

    const channel = supabase
      ?.channel(`presence-${instituteUserId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${instituteUserId}`,
        },
        (payload) => {
          const lastSeen = payload?.new?.last_seen;
          if (!lastSeen) {
            onStatusChange?.(false);
            return;
          }
          const diffMinutes = (new Date() - new Date(lastSeen)) / (1000 * 60);
          onStatusChange?.(diffMinutes <= ONLINE_THRESHOLD_MINUTES);
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },
};
